import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
    Trash2,
    CheckCircle2,
    ShieldAlert,
    Clock,
    Mail,
    HelpCircle,
} from "lucide-react";

export default function DeleteAccountPage() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState("");
    const [status, setStatus] = useState({ type: "", text: "" });

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);
        const email = formData.get("email");

        setIsSubmitting(true);
        setStatus({ type: "", text: "" });

        try {
            const response = await fetch(
                "https://formsubmit.co/ajax/rootpointersfrontend@gmail.com",
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to submit request");
            }

            setSubmittedEmail(email ? String(email) : "");
            setIsSubmitted(true);
            form.reset();
        } catch (error) {
            setStatus({
                type: "error",
                text: "Unable to submit your request at this moment. Please try again or email us directly at support@genesisrecoveryai.com.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-[#f8f5ef] text-slate-900 min-h-screen flex flex-col">
            <Navbar />

            <main data-testid="delete-account-page" className="flex-1 pt-[7rem]">
                {/* Hero Banner */}
                <section className="relative overflow-hidden bg-gradient-to-r from-emerald-900 via-teal-800 to-emerald-700 text-white border-b border-emerald-900/30">
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_left,_white_0%,_transparent_40%),radial-gradient(circle_at_bottom_right,_white_0%,_transparent_35%)]" />
                    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14 text-center">
                        <h1 className="font-serif text-3xl md:text-5xl tracking-tight">
                            Request Account Deletion
                        </h1>
                    </div>
                </section>

                {/* Main Content Area */}
                <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                        {/* Form Column */}
                        <div className="lg:col-span-7">
                            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-6 sm:p-8">
                                {isSubmitted ? (
                                    <div
                                        data-testid="deletion-success-message"
                                        className="text-center py-6"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-sm">
                                            <CheckCircle2 className="w-9 h-9" />
                                        </div>
                                        <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-2 border border-emerald-200">
                                            Request Received
                                        </span>
                                        <h2 className="text-2xl font-bold text-slate-900 mb-3">
                                            Deletion Request Submitted
                                        </h2>
                                        <p className="text-slate-600 text-sm leading-relaxed max-w-md mx-auto mb-6">
                                            We have received your account deletion request for{" "}
                                            <strong className="text-slate-900 font-semibold">
                                                {submittedEmail || "your account"}
                                            </strong>
                                            . Our data protection team will verify the request and
                                            permanently purge your account within 30 days.
                                        </p>

                                        <div className="bg-slate-50 rounded-2xl border border-slate-200/80 p-5 text-left mb-6 space-y-3">
                                            <div className="flex items-start gap-3">
                                                <Clock className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                                                <div className="text-xs text-slate-600 leading-relaxed">
                                                    <span className="font-semibold text-slate-800">
                                                        30-Day Processing Period:
                                                    </span>{" "}
                                                    A confirmation notice will be sent to your
                                                    email address. Your data will be scheduled for
                                                    permanent removal from our databases.
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                <HelpCircle className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                                                <div className="text-xs text-slate-600 leading-relaxed">
                                                    <span className="font-semibold text-slate-800">
                                                        Changed your mind?
                                                    </span>{" "}
                                                    If you submitted this by mistake, contact our
                                                    support team within 14 days to cancel the
                                                    request.
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsSubmitted(false);
                                                setSubmittedEmail("");
                                            }}
                                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-4"
                                        >
                                            Submit another request
                                        </button>
                                    </div>
                                ) : (
                                    <form
                                        data-testid="delete-account-form"
                                        onSubmit={handleSubmit}
                                        className="space-y-5"
                                    >
                                        <input
                                            type="hidden"
                                            name="_subject"
                                            value="Account Deletion Request - Genesis Recovery AI"
                                        />
                                        <input type="hidden" name="_captcha" value="false" />
                                        <input
                                            type="hidden"
                                            name="request_type"
                                            value="account_deletion"
                                        />

                                        <div>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-semibold text-slate-900"
                                                >
                                                    Registered Email Address{" "}
                                                    <span className="text-rose-500">*</span>
                                                </label>
                                            </div>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                required
                                                placeholder="your-account-email@example.com"
                                                className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                                            />
                                            <p className="text-xs text-slate-500 mt-1.5">
                                                Enter the email address associated with your Genesis
                                                Recovery AI account.
                                            </p>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="fullName"
                                                className="block text-sm font-semibold text-slate-900 mb-1.5"
                                            >
                                                Full Name / Username{" "}
                                                <span className="text-slate-400 font-normal">
                                                    (Optional)
                                                </span>
                                            </label>
                                            <input
                                                id="fullName"
                                                name="fullName"
                                                type="text"
                                                placeholder="Your name or username"
                                                className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                                            />
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="reason"
                                                className="block text-sm font-semibold text-slate-900 mb-1.5"
                                            >
                                                Reason for Deletion{" "}
                                                <span className="text-slate-400 font-normal">
                                                    (Optional)
                                                </span>
                                            </label>
                                            <select
                                                id="reason"
                                                name="reason"
                                                defaultValue=""
                                                className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                                            >
                                                <option value="" disabled>
                                                    Select a reason (optional)
                                                </option>
                                                <option value="no_longer_using">
                                                    No longer using the app
                                                </option>
                                                <option value="duplicate_account">
                                                    Created a duplicate account
                                                </option>
                                                <option value="reset_journey">
                                                    Starting fresh / Resetting my journey
                                                </option>
                                                <option value="other">Other reason</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="additionalNotes"
                                                className="block text-sm font-semibold text-slate-900 mb-1.5"
                                            >
                                                Additional Notes{" "}
                                                <span className="text-slate-400 font-normal">
                                                    (Optional)
                                                </span>
                                            </label>
                                            <textarea
                                                id="additionalNotes"
                                                name="additionalNotes"
                                                rows={3}
                                                placeholder="Any additional details or feedback for our team..."
                                                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                                            />
                                        </div>

                                        {/* Confirmation Agreement */}
                                        <div className="rounded-2xl border border-rose-200/90 bg-rose-50/60 p-4">
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    required
                                                    name="confirmDeletion"
                                                    id="confirmDeletion"
                                                    className="mt-1 h-4 w-4 rounded border-rose-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                                                />
                                                <span className="text-xs text-rose-900 font-medium leading-relaxed">
                                                    I understand that submitting this request will
                                                    permanently and irreversibly delete my Genesis
                                                    Recovery AI account, including my recovery
                                                    journals, check-in history, progress
                                                    milestones, and personal data.
                                                </span>
                                            </label>
                                        </div>

                                        {status.text && (
                                            <div
                                                className={`p-3.5 rounded-xl text-xs font-medium ${
                                                    status.type === "success"
                                                        ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                                                        : "bg-rose-50 text-rose-800 border border-rose-200"
                                                }`}
                                            >
                                                {status.text}
                                            </div>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold shadow-sm hover:shadow transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    <span>Submitting Request...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Trash2 className="w-4 h-4" />
                                                    <span>Submit Account Deletion Request</span>
                                                </>
                                            )}
                                        </button>

                                        <p className="text-[11px] text-center text-slate-500">
                                            By submitting this form, you initiate a permanent data
                                            erasure process in accordance with our{" "}
                                            <Link
                                                to="/privacy-policy"
                                                className="text-emerald-700 hover:underline font-medium"
                                            >
                                                Privacy Policy
                                            </Link>
                                            .
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Sidebar: Information & Compliance Details */}
                        <div className="lg:col-span-5 space-y-6">
                            {/* What Gets Deleted Card */}
                            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                                        <ShieldAlert className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900 text-sm">
                                            What will be deleted?
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Permanent data removal
                                        </p>
                                    </div>
                                </div>
                                <ul className="space-y-2.5 text-xs text-slate-600 leading-relaxed">
                                    <li className="flex items-start gap-2">
                                        <span className="text-rose-500 font-bold">•</span>
                                        <span>
                                            <strong className="text-slate-800 font-medium">
                                                Profile &amp; Credentials:
                                            </strong>{" "}
                                            Your email, username, phone number, and authentication
                                            credentials.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-rose-500 font-bold">•</span>
                                        <span>
                                            <strong className="text-slate-800 font-medium">
                                                Private Journals &amp; Logs:
                                            </strong>{" "}
                                            All journal entries, reflections, and thoughts stored in
                                            the app.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-rose-500 font-bold">•</span>
                                        <span>
                                            <strong className="text-slate-800 font-medium">
                                                Hope AI Conversations:
                                            </strong>{" "}
                                            All chat histories and interactive support records.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-rose-500 font-bold">•</span>
                                        <span>
                                            <strong className="text-slate-800 font-medium">
                                                Streaks &amp; Milestones:
                                            </strong>{" "}
                                            Daily check-ins, sober counter metrics, and badges.
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* Support & Timeline Box */}
                            <div className="bg-emerald-50/70 rounded-3xl border border-emerald-200/70 p-6 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                                        <Clock className="w-5 h-5" />
                                    </div>
                                    <div className="text-xs text-emerald-900/90 leading-relaxed pt-0.5">
                                        <span className="font-semibold text-emerald-950 block text-sm mb-0.5">
                                            Processing Timeline
                                        </span>
                                        In compliance with Apple App Store and Google Play Store
                                        policies, web requests are processed within{" "}
                                        <strong className="text-emerald-950 font-bold">30 days</strong> of
                                        identity verification.
                                    </div>
                                </div>

                                <div className="border-t border-emerald-200/80 pt-4 flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div className="text-xs text-emerald-900/90 leading-relaxed pt-0.5">
                                        <span className="font-semibold text-emerald-950 block text-sm mb-0.5">
                                            Need Help or Want to Cancel?
                                        </span>
                                        You can reach out directly to our privacy desk at{" "}
                                        <a
                                            href="mailto:support@genesisrecoveryai.com"
                                            className="text-emerald-800 font-bold hover:text-emerald-950 underline underline-offset-2"
                                        >
                                            support@genesisrecoveryai.com
                                        </a>
                                        .
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
